/**
 * The coloured weather pictures of the weather card (0.32.0).
 *
 * **These are pictures, not colours of the appearance** -- from use: "bunte
 * Symbole wie Apple Weather in iOS." A sun is yellow and a rain drop is blue
 * on a light ground and on a dark one alike, the way a photo keeps its
 * colours. That is the one exception `CLAUDE.md` grants to "Farben stehen in
 * `tokens.css`": what lies on a picture belongs to no appearance. The
 * outline icons in `Icons.tsx` stay for the highlight (`WetterZeichen`),
 * where a sign has to follow the text colour.
 *
 * All drawn on a 24 × 24 grid; `size` scales. Night is a moon instead of the
 * sun for clear skies; the moon's face follows its phase.
 */
import type { Lage } from '../lib/wetterlage'

interface BildProps {
  size?: number
  className?: string
  title?: string
}

const SUN = '#f5b400'
const SUN_RAY = '#f7c948'
const CLOUD = '#ffffff'
const CLOUD_EDGE = '#b9bfc9'
/* The rain cloud: light grey, not dark -- from use, on the card's blue face
   (#7f92ad) a dark grey cloud was "kaum sichtbar". White is the fair-weather
   cloud; this one stays a shade below it. */
const CLOUD_DARK = '#c9d0da'
const RAIN = '#3f8ef5'
const SNOW = '#7fc6ff'
const BOLT = '#ffcf33'
const FOG = '#aab2bf'
const MOON = '#f4e9b5'
const MOON_SHADE = '#5b6270'
const SKY_NIGHT = '#3b4d7a'
const HEAT = '#e5484d'
const WIND = '#6f9dd8'
const HORIZON = '#c9ced8'

function Bild({ size = 24, className, title, children }: BildProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      aria-hidden={title === undefined ? true : undefined}
      role={title === undefined ? undefined : 'img'}
      focusable="false"
    >
      {title !== undefined && <title>{title}</title>}
      {children}
    </svg>
  )
}

const Sonne = ({ cx, cy, r }: { cx: number; cy: number; r: number }) => (
  <g>
    {Array.from({ length: 8 }, (_, i) => {
      const a = (i * Math.PI) / 4
      const x1 = cx + Math.cos(a) * (r + 1.4)
      const y1 = cy + Math.sin(a) * (r + 1.4)
      const x2 = cx + Math.cos(a) * (r + 3.4)
      const y2 = cy + Math.sin(a) * (r + 3.4)
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={SUN_RAY} strokeWidth="1.6" strokeLinecap="round" />
    })}
    <circle cx={cx} cy={cy} r={r} fill={SUN} />
  </g>
)

const Wolke = ({ x = 0, y = 0, s = 1, dark = false }: { x?: number; y?: number; s?: number; dark?: boolean }) => (
  <path
    transform={`translate(${x} ${y}) scale(${s})`}
    d="M18.5 19H7a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 17.9 8.4 5.3 5.3 0 0 1 18.5 19Z"
    fill={dark ? CLOUD_DARK : CLOUD}
    stroke={dark ? '#eef1f5' : CLOUD_EDGE}
    strokeWidth="1"
    strokeLinejoin="round"
  />
)

const Tropfen = ({ x, y }: { x: number; y: number }) => (
  <path d={`M${x} ${y}c-1.2 1.8-1.8 2.9-1.8 3.7a1.8 1.8 0 0 0 3.6 0c0-.8-.6-1.9-1.8-3.7Z`} fill={RAIN} />
)

const Flocke = ({ x, y }: { x: number; y: number }) => (
  <g stroke={SNOW} strokeWidth="1.1" strokeLinecap="round">
    <line x1={x} y1={y - 2} x2={x} y2={y + 2} />
    <line x1={x - 1.7} y1={y - 1} x2={x + 1.7} y2={y + 1} />
    <line x1={x - 1.7} y1={y + 1} x2={x + 1.7} y2={y - 1} />
  </g>
)

/** The moon with its lit part -- eight phases, waxing lit on the right. */
export function MondBild({ phase, size, className, title }: BildProps & { phase: string | null }) {
  const r = 9
  const cx = 12
  const cy = 12
  const lit = (() => {
    switch (phase) {
      case 'new': return null
      case 'full': return <circle cx={cx} cy={cy} r={r} fill={MOON} />
      case 'firstQuarter': return <path d={`M${cx} ${cy - r}A${r} ${r} 0 0 1 ${cx} ${cy + r}Z`} fill={MOON} />
      case 'thirdQuarter': return <path d={`M${cx} ${cy - r}A${r} ${r} 0 0 0 ${cx} ${cy + r}Z`} fill={MOON} />
      case 'waxingCrescent': return <path d={`M${cx} ${cy - r}A${r} ${r} 0 0 1 ${cx} ${cy + r}A${r / 2} ${r} 0 0 1 ${cx} ${cy - r}Z`} fill={MOON} />
      case 'waxingGibbous': return <path d={`M${cx} ${cy - r}A${r} ${r} 0 0 1 ${cx} ${cy + r}A${r / 2} ${r} 0 0 0 ${cx} ${cy - r}Z`} fill={MOON} />
      case 'waningGibbous': return <path d={`M${cx} ${cy - r}A${r} ${r} 0 0 0 ${cx} ${cy + r}A${r / 2} ${r} 0 0 1 ${cx} ${cy - r}Z`} fill={MOON} />
      case 'waningCrescent': return <path d={`M${cx} ${cy - r}A${r} ${r} 0 0 0 ${cx} ${cy + r}A${r / 2} ${r} 0 0 0 ${cx} ${cy - r}Z`} fill={MOON} />
      default: return <circle cx={cx} cy={cy} r={r} fill={MOON} />
    }
  })()
  return (
    <Bild size={size} className={className} title={title}>
      <circle cx={cx} cy={cy} r={r} fill={MOON_SHADE} />
      {lit}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={MOON_SHADE} strokeWidth="0.6" />
    </Bild>
  )
}

/** The big picture of the card: the condition, by day or by night. */
export function Wetterbild({ lage, daylight, size, className, title }: BildProps & { lage: Lage | null; daylight: boolean | null }) {
  const night = daylight === false
  return (
    <Bild size={size} className={className} title={title}>
      {lage === 'klar' && !night && <Sonne cx={12} cy={12} r={5.5} />}
      {lage === 'klar' && night && (
        <g>
          <path d="M15.5 3.5a8.5 8.5 0 1 0 5 15.3A8 8 0 0 1 15.5 3.5Z" fill={MOON} stroke={MOON_SHADE} strokeWidth="0.6" />
          <circle cx="6" cy="6" r="1" fill={SUN_RAY} />
          <circle cx="4.5" cy="11" r="0.7" fill={SUN_RAY} />
        </g>
      )}
      {lage === 'teils' && (
        <g>
          {night
            ? <path d="M9.5 2.5a5.5 5.5 0 1 0 3.5 9.7A5.2 5.2 0 0 1 9.5 2.5Z" fill={MOON} stroke={MOON_SHADE} strokeWidth="0.5" />
            : <Sonne cx={8} cy={8} r={3.6} />}
          <Wolke x={2} y={2} s={0.9} />
        </g>
      )}
      {lage === 'wolkig' && (
        <g>
          <Wolke x={-1} y={-4} s={0.75} dark />
          <Wolke x={0} y={1} s={0.95} />
        </g>
      )}
      {lage === 'regen' && (
        <g>
          <Wolke x={0} y={-4} s={0.95} dark />
          <Tropfen x={8} y={14} />
          <Tropfen x={12.5} y={16} />
          <Tropfen x={17} y={14} />
        </g>
      )}
      {lage === 'schnee' && (
        <g>
          <Wolke x={0} y={-4} s={0.95} />
          <Flocke x={8} y={17} />
          <Flocke x={13} y={19} />
          <Flocke x={17.5} y={16.5} />
        </g>
      )}
      {lage === 'gewitter' && (
        <g>
          <Wolke x={0} y={-5} s={0.95} dark />
          <path d="M13 10l-4 6h3l-1.5 5.5L16 14h-3l1.5-4Z" fill={BOLT} />
        </g>
      )}
      {lage === 'nebel' && (
        <g stroke={FOG} strokeWidth="1.8" strokeLinecap="round">
          <line x1="4" y1="8" x2="20" y2="8" />
          <line x1="6" y1="12" x2="18" y2="12" />
          <line x1="4" y1="16" x2="20" y2="16" />
        </g>
      )}
      {lage === 'wind' && <WindBild size={24} className={undefined} />}
      {lage === null && <Wolke x={0} y={1} s={0.95} />}
    </Bild>
  )
}

export function WindBild({ size, className, title }: BildProps) {
  return (
    <Bild size={size} className={className} title={title}>
      <g fill="none" stroke={WIND} strokeWidth="1.8" strokeLinecap="round">
        <path d="M3 9h11a2.5 2.5 0 1 0-2.5-2.5" />
        <path d="M3 13.5h15a2.5 2.5 0 1 1-2.5 2.5" />
        <path d="M3 18h8a2 2 0 1 1-2 2" />
      </g>
    </Bild>
  )
}

export function ThermometerBild({ size, className, title }: BildProps) {
  return (
    <Bild size={size} className={className} title={title}>
      <rect x="9.5" y="3" width="5" height="12" rx="2.5" fill="none" stroke={CLOUD_DARK} strokeWidth="1.4" />
      <circle cx="12" cy="17.5" r="3.5" fill={HEAT} />
      <rect x="11" y="8" width="2" height="8" fill={HEAT} />
    </Bild>
  )
}

export function FeuchteBild({ size, className, title }: BildProps) {
  return (
    <Bild size={size} className={className} title={title}>
      <path d="M12 3c-3.6 5.2-5.5 8.4-5.5 10.9a5.5 5.5 0 0 0 11 0C17.5 11.4 15.6 8.2 12 3Z" fill="none" stroke={RAIN} strokeWidth="1.6" />
      <path d="M9.5 16.5l5-5" stroke={RAIN} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="9.8" cy="12" r="1" fill={RAIN} />
      <circle cx="14.2" cy="16.2" r="1" fill={RAIN} />
    </Bild>
  )
}

const Horizont = () => <line x1="3" y1="18" x2="21" y2="18" stroke={HORIZON} strokeWidth="1.6" strokeLinecap="round" />

export function SonnenaufgangBild({ size, className, title }: BildProps) {
  return (
    <Bild size={size} className={className} title={title}>
      <path d="M6.5 18a5.5 5.5 0 0 1 11 0Z" fill={SUN} />
      <g stroke={SUN_RAY} strokeWidth="1.5" strokeLinecap="round">
        <line x1="12" y1="6" x2="12" y2="8.5" />
        <line x1="5" y1="10" x2="6.8" y2="11.8" />
        <line x1="19" y1="10" x2="17.2" y2="11.8" />
      </g>
      <Horizont />
      <path d="M12 2.5l-2.5 2.5h5Z" fill={CLOUD_DARK} />
    </Bild>
  )
}

export function SonnenuntergangBild({ size, className, title }: BildProps) {
  return (
    <Bild size={size} className={className} title={title}>
      <path d="M6.5 18a5.5 5.5 0 0 1 11 0Z" fill="#f28c28" />
      <g stroke="#f4a259" strokeWidth="1.5" strokeLinecap="round">
        <line x1="5" y1="10" x2="6.8" y2="11.8" />
        <line x1="19" y1="10" x2="17.2" y2="11.8" />
      </g>
      <Horizont />
      <path d="M12 5.5l-2.5-2.5h5Z" fill={CLOUD_DARK} />
    </Bild>
  )
}

export { SKY_NIGHT }

/**
 * The compass of the wind tile (0.35.0): ticks, the four letters from the
 * texts, an arrow that points where the wind goes -- it comes *from*
 * `degrees` -- and the speed in the middle.
 */
export function KompassBild({ degrees, speed, letters, size = 96, title }: { degrees: number; speed: string; letters: { n: string; o: string; s: string; w: string }; size?: number; title?: string }) {
  const c = 50
  const r = 44
  const a = ((degrees + 180) * Math.PI) / 180
  const tip = { x: c + Math.sin(a) * (r - 4), y: c - Math.cos(a) * (r - 4) }
  const tail = { x: c - Math.sin(a) * (r - 4), y: c + Math.cos(a) * (r - 4) }
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={title} focusable="false">
      {Array.from({ length: 72 }, (_, i) => {
        const t = (i * Math.PI) / 36
        const long = i % 18 === 0
        const inner = long ? r - 7 : r - 3.5
        return <line key={i} x1={c + Math.sin(t) * r} y1={c - Math.cos(t) * r} x2={c + Math.sin(t) * inner} y2={c - Math.cos(t) * inner} stroke="rgba(255,255,255,0.55)" strokeWidth={long ? 1.6 : 0.9} />
      })}
      <text x={c} y="13" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">{letters.n}</text>
      <text x="90" y="53" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">{letters.o}</text>
      <text x={c} y="93" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">{letters.s}</text>
      <text x="10" y="53" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">{letters.w}</text>
      <line x1={tail.x} y1={tail.y} x2={tip.x} y2={tip.y} stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      <circle cx={tail.x} cy={tail.y} r="3.5" fill="#fff" />
      <path d={`M${tip.x} ${tip.y} l${-Math.sin(a) * 7 - Math.cos(a) * 4} ${Math.cos(a) * 7 - Math.sin(a) * 4} M${tip.x} ${tip.y} l${-Math.sin(a) * 7 + Math.cos(a) * 4} ${Math.cos(a) * 7 + Math.sin(a) * 4}`} stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" />
      <circle cx={c} cy={c} r="17" fill="rgba(255,255,255,0.18)" />
      <text x={c} y="54" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily="system-ui, sans-serif">{speed}</text>
    </svg>
  )
}

/** The location arrow of the widget (0.36.0): the card stands where the device stood. */
export function OrtungBild({ size, className, title }: BildProps) {
  return (
    <Bild size={size} className={className} title={title}>
      <path d="M20.5 3.5 3.8 10.4a.8.8 0 0 0 .1 1.5l7 1.9 1.9 7a.8.8 0 0 0 1.5.1l6.9-16.7a.8.8 0 0 0-.7-.7Z" fill="#fff" />
    </Bild>
  )
}
